import React from "react";
import styled, { withTheme } from "styled-components";

import KCSpinner from "Components/common/KCSpinner";

const StyledErrorLabel = styled.div`
  opacity: ${(props) => (props.isShown ? 1 : 0)};
  transition: opacity 0.2s;
`;

const StyledButton = styled.div`
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: ${(props) => (props.isFullWidth ? "100%" : "auto")};
  pointer-events: ${(props) => (props.isDisabled ? "none" : "default")};
  padding: ${(props) =>
    (props.size === "xs" && "0.2rem 0.8rem") ||
    (props.size === "small" && "0.4rem 1rem") ||
    (props.size === "medium" && "0.6rem 1.4rem") ||
    (props.size === "large" && "0.6rem 1.8rem")};
  font-weight: 600;
  font-size: ${(props) =>
    (props.size === "xs" && "0.6rem") ||
    (props.size === "small" && "0.8rem") ||
    (props.size === "medium" && "1.2rem") ||
    (props.size === "large" && "2.4rem")};
  border-radius: ${(props) => (props.isRounded ? 3 : 0)}px;
  border-width: ${(props) =>
    (props.size === "xs" && "1px") ||
    (props.size === "small" && "1px") ||
    (props.size === "medium" && "2px") ||
    (props.size === "large" && "2px")};
  border-style: solid;
  border-color: ${(props) => props.borderColor};
  background-color: ${(props) =>
    props.isError
      ? (props.buttonType === "default" && "transparent") ||
        props.theme.color.red
      : props.backgroundColor};
  transition: background-color 0.2s;
  color: ${(props) => props.labelColor};

  ${StyledErrorLabel} {
    color: ${(props) =>
      (props.buttonType === "default" && props.theme.color.red) ||
      props.theme.color.white};
  }

  &:hover {
    cursor: pointer;
    background-color: ${(props) =>
      (props.buttonType === "default" && "transparent") ||
      (props.buttonType === "primary" && props.isError
        ? props.theme.color.redLight
        : props.theme.color.dimGrey) ||
      (props.buttonType === "secondary" && props.theme.color.dimGrey)};
    ${(props) => props.isInvert && `color: white !important;`}
    ${(props) =>
      props.buttonType === "default" &&
      `
            color: ${props.theme.color.grey};
            border-color: ${props.theme.color.grey};
            ${StyledErrorLabel} {
                color: ${props.theme.color.grey};
            }
            `}
  }
`;

const StyledLabel = styled.div`
  opacity: ${(props) => (props.isShown ? 1 : 0)};
  transition: opacity 0.2s;
  text-transform: uppercase;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                supported by Chrome, Edge, Opera and Firefox */
`;

const StyledContentWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const KCButton = ({
  buttonType = "default",
  backgroundColor,
  labelColor,
  label,
  errorLabel = "ERROR",
  size = "medium",
  isError = false,
  isLoading = false,
  handleClick,
  isInvert,
  isFullWidth = false,
  isDisabled = false,
  isRounded = true,
  ...props
}) => {
  var buttonTheme;
  switch (buttonType) {
    case "primary":
      buttonTheme = {
        labelColor:
          (labelColor && props.theme.color[labelColor]) ||
          (isInvert ? props.theme.color.black : props.theme.color.white),
        loadingColor: isLoading ? props.theme.color.white : "transparent",
        backgroundColor: isError
          ? props.theme.color.reds
          : (backgroundColor && props.theme.color[backgroundColor]) ||
            (isInvert ? props.theme.color.white : props.theme.color.black),
        borderColor: "transparent",
      };
      break;
    case "secondary":
      buttonTheme = {
        labelColor:
          (labelColor && props.theme.color[labelColor]) ||
          props.theme.color.white,
        loadingColor: isLoading ? props.theme.color.white : "transparent",
        backgroundColor: isError
          ? props.theme.color.red
          : (backgroundColor && props.theme.color[backgroundColor]) ||
            props.theme.color.grey,
        borderColor: "transparent",
      };
      break;
    default:
      buttonTheme = {
        labelColor: labelColor || props.theme.color.dimGrey,
        loadingColor: isLoading ? props.theme.color.white : "transparent",
        backgroundColor: "transparent",
        borderColor: isError
          ? props.theme.color.red
          : labelColor || props.theme.color.dimGrey,
      };
  }

  return (
    <StyledButton
      buttonType={buttonType}
      labelColor={buttonTheme.labelColor}
      backgroundColor={buttonTheme.backgroundColor}
      borderColor={props.borderColor ? props.borderColor : buttonTheme.borderColor}
      size={size}
      onClick={() => handleClick()}
      isError={isError}
      isLoading={isLoading}
      isInvert={isInvert}
      isDisabled={isDisabled}
      isFullWidth={isFullWidth}
      isRounded={isRounded}
    >
      <StyledLabel isShown={!isError && !isLoading}>{label}</StyledLabel>
      <StyledContentWrapper>
        <StyledErrorLabel isShown={isError}>{errorLabel}</StyledErrorLabel>
      </StyledContentWrapper>
      <StyledContentWrapper>
        <KCSpinner
          color={buttonTheme.labelColor}
          isShown={isLoading && !isError}
          size={size}
        />
      </StyledContentWrapper>
    </StyledButton>
  );
};

export default withTheme(KCButton);
